import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  Tooltip,
  DialogActions,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fade,
  Paper,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PeopleIcon from "@mui/icons-material/People";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import BASE_URL from "BASE_URL";

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterMobile, setFilterMobile] = useState(""); // Added mobile filter state
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMembership, setFilterMembership] = useState("");
  const [filterActive, setFilterActive] = useState("");

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/admin/allusers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setUsers(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch users", "error");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      showSnackbar(error.response?.data?.message || "Error fetching users", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filtered users based on all criteria
  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    if (filterName) {
      filtered = filtered.filter((user) =>
        user.name?.toLowerCase().includes(filterName.toLowerCase())
      );
    }
    if (filterEmail) {
      filtered = filtered.filter((user) =>
        user.email_id?.toLowerCase().includes(filterEmail.toLowerCase())
      );
    }
    // Added mobile number filter
    if (filterMobile) {
      filtered = filtered.filter((user) => {
        const mobileNumber = user.mobile_number || "";
        const whatsappNumber = user.whatsapp_number || "";
        return (
          mobileNumber.toLowerCase().includes(filterMobile.toLowerCase()) ||
          whatsappNumber.toLowerCase().includes(filterMobile.toLowerCase())
        );
      });
    }
    if (filterStatus) {
      filtered = filtered.filter((user) => user.status === filterStatus);
    }
    if (filterMembership) {
      filtered = filtered.filter((user) => user.membership_plan_status === filterMembership);
    }
    if (filterActive !== "") {
      filtered = filtered.filter((user) => user.is_active === (filterActive === "active"));
    }

    return filtered;
  }, [users, filterName, filterEmail, filterMobile, filterStatus, filterMembership, filterActive]);

  // Clear all filters
  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilterMobile(""); // Added mobile clear
    setFilterStatus("");
    setFilterMembership("");
    setFilterActive("");
  };

  const handleStatusChange = (userId, currentStatus) => {
    setSelectedUserId(userId);
    setStatusAction(currentStatus ? "deactivate" : "activate");
    setStatusDialogOpen(true);
  };

  const confirmStatusUpdate = async () => {
    setIsUpdating(true);
    try {
      const response = await axios.put(
        `${BASE_URL}/admin/update-user-status`,
        {
          userId: selectedUserId,
          is_active: statusAction === "activate" ? true : false,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        showSnackbar(
          `User ${statusAction === "activate" ? "activated" : "deactivated"} successfully!`,
          "success"
        );
        fetchUsers();
        setStatusDialogOpen(false);
      } else {
        showSnackbar(response.data.message || "Status update failed", "error");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showSnackbar(error.response?.data?.message || "Failed to update user status", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusChip = (status, isActive) => {
    if (!isActive) {
      return <Chip label="Inactive" size="small" color="error" variant="outlined" />;
    }
    switch (status) {
      case "active":
        return <Chip label="Active" size="small" color="success" />;
      case "rejected":
        return <Chip label="Rejected" size="small" color="error" />;
      case "pending":
        return <Chip label="Pending" size="small" color="warning" />;
      default:
        return <Chip label={status} size="small" color="default" />;
    }
  };

  const getMembershipChip = (plan) => {
    switch (plan) {
      case "paid":
        return <Chip label="PREMIUM" size="small" color="primary" />;
      case "free":
        return <Chip label="FREE" size="small" color="info" />;
      default:
        return <Chip label="FREE" size="small" color="default" />;
    }
  };

  const columns = [
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="black">
          USER
        </MDTypography>
      ),
      accessor: "user",
      align: "left",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="black">
          CONTACT
        </MDTypography>
      ),
      accessor: "contact",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="black">
          COMPANY
        </MDTypography>
      ),
      accessor: "company",
      align: "left",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="black">
          MEMBERSHIP
        </MDTypography>
      ),
      accessor: "membership",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="black">
          STATUS
        </MDTypography>
      ),
      accessor: "status",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="black">
          JOINED
        </MDTypography>
      ),
      accessor: "joined",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="black">
          ACTIONS
        </MDTypography>
      ),
      accessor: "action",
      align: "center",
    },
  ];

  const rows = useMemo(
    () =>
      filteredUsers.map((user, index) => ({
        user: (
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <Avatar
              src={user.profile_photo}
              alt={user.name || user.email_id}
              sx={{ width: 40, height: 40, borderRadius: "50%" }}
            />
            <MDBox>
              <MDTypography variant="button" fontWeight="bold" color="dark">
                {user.name || "Not provided"}
              </MDTypography>
              <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
                {user.email_id}
              </MDTypography>
            </MDBox>
          </MDBox>
        ),
        contact: (
          <MDBox>
            {user.mobile_number && (
              <MDTypography variant="caption" display="block" fontWeight="medium">
                📱 {user.mobile_number}
              </MDTypography>
            )}
            {user.whatsapp_number && user.whatsapp_number !== user.mobile_number && (
              <MDTypography variant="caption" color="text" display="block" fontWeight="medium">
                💬 {user.whatsapp_number}
              </MDTypography>
            )}
            {!user.mobile_number && !user.whatsapp_number && (
              <MDTypography variant="caption" fontWeight="medium" color="text">
                Not provided
              </MDTypography>
            )}
          </MDBox>
        ),
        company: (
          <MDBox>
            {user.company_name ? (
              <>
                <MDTypography variant="caption" fontWeight="medium" display="block">
                  {user.company_name}
                </MDTypography>
                {user.gst_number && (
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="medium"
                    fontSize="0.7rem"
                  >
                    Business Type: {user.business_type}
                  </MDTypography>
                )}
              </>
            ) : (
              <MDTypography variant="caption" fontWeight="medium" color="text">
                Not provided
              </MDTypography>
            )}
          </MDBox>
        ),
        membership: (
          <MDBox textAlign="center">{getMembershipChip(user.membership_plan_status)}</MDBox>
        ),
        status: <MDBox textAlign="center">{getStatusChip(user.status, user.is_active)}</MDBox>,
        joined: (
          <MDTypography variant="caption" fontWeight="medium" textAlign="center">
            {formatDate(user.createdAt)}
          </MDTypography>
        ),
        action: (
          <MDBox display="flex" justifyContent="center" gap={0.5}>
            <Tooltip title="View Details">
              <IconButton
                color="info"
                onClick={() => navigate(`/users-details/${user._id}`)}
                size="small"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      })),
    [filteredUsers]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "20px" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <PeopleIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    User Management
                  </MDTypography>
                </MDBox>
              </MDBox>

              {/* Filter Section */}
              {!showFilters && (
                <Fade in={!showFilters}>
                  <Box sx={{ pt: 3, pl: 3 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} md={2.2}>
                        <TextField
                          fullWidth
                          label="Name"
                          value={filterName}
                          onChange={(e) => setFilterName(e.target.value)}
                          placeholder="Search by name"
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Email"
                          value={filterEmail}
                          onChange={(e) => setFilterEmail(e.target.value)}
                          placeholder="Search by email"
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        />
                      </Grid>
                      {/* Added Mobile Number Filter Field */}
                      <Grid item xs={12} md={2.2}>
                        <TextField
                          fullWidth
                          label="Mobile Number"
                          value={filterMobile}
                          onChange={(e) => setFilterMobile(e.target.value)}
                          placeholder="Search by mobile"
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            label="Status"
                            sx={{ borderRadius: "10px", height: "40px" }}
                          >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={1.8}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Membership</InputLabel>
                          <Select
                            value={filterMembership}
                            onChange={(e) => setFilterMembership(e.target.value)}
                            label="Membership"
                            sx={{ borderRadius: "10px", height: "40px" }}
                          >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="free">Free</MenuItem>
                            <MenuItem value="paid">Paid</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={1.4} mr={0}>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                          fullWidth
                          sx={{ borderRadius: "10px", height: "40px" }}
                        >
                          Clear
                        </MDButton>
                      </Grid>
                    </Grid>
                  </Box>
                </Fade>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : filteredUsers.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      No users found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxWidth: "400px",
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Box sx={{ mb: 2 }}>
            {statusAction === "activate" ? (
              <CheckCircleIcon sx={{ fontSize: 60, color: "#4caf50" }} />
            ) : (
              <BlockIcon sx={{ fontSize: 60, color: "#ef4444" }} />
            )}
          </Box>
          <MDTypography variant="h5" fontWeight="bold" mb={2}>
            {statusAction === "activate" ? "Activate User?" : "Deactivate User?"}
          </MDTypography>
          <MDTypography variant="body2" color="text" mb={3}>
            {statusAction === "activate"
              ? "This user will be able to access their account and use all features."
              : "This user will be blocked from accessing their account. They won't be able to log in."}
          </MDTypography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => setStatusDialogOpen(false)}
              disabled={isUpdating}
            >
              Cancel
            </MDButton>
            <MDButton
              variant="contained"
              color={statusAction === "activate" ? "success" : "error"}
              onClick={confirmStatusUpdate}
              disabled={isUpdating}
              startIcon={isUpdating && <CircularProgress size={20} color="inherit" />}
            >
              {isUpdating
                ? "Processing..."
                : statusAction === "activate"
                ? "Activate"
                : "Deactivate"}
            </MDButton>
          </Stack>
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default UserManagement;
